import { Clear, Save } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { masterAPI } from "../../../Masters/dataConfig";
import { axiosPrivate } from "../../../../axios/axios";
import { color } from "../../../../StyleData/Config";
import { toast } from "react-toastify";

const PartEdit = ({ editData, handleCloseClick, type }) => {
  const [quantity, setQuantity] = useState(editData?.quantity ?? "");
  const [maxQuantity, setMaxQuantity] = useState("");
  const [loadingMaxQuantity, setLoadingMaxQuantity] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editData?.partId && editData?.stockRoomId) {
      getMaxQuantity();
    }
  }, [editData?.partId, editData?.stockRoomId]);

  // Get available balance based on Part + Stock Room
  const getMaxQuantity = async () => {
    setLoadingMaxQuantity(true);

    try {
      const reservationQtyLink =
        masterAPI["inventory"].getPartsReservationQuantity;

      const res = await axiosPrivate.get(
        `${reservationQtyLink}/${editData.partId}/${editData.stockRoomId}`
      );

      const result = res.data?.result;

      const balanceQuantity =
        typeof result === "number"
          ? result
          : Array.isArray(result)
          ? result[0]?.balanceQuantity ??
            result[0]?.quantity ??
            ""
          : result?.balanceQuantity ??
            result?.quantity ??
            "";

      /*
       * While editing, the current quantity is already part of the
       * planned/reserved quantity.
       *
       * Add the existing quantity back to the available balance
       * so the user can keep the existing quantity.
       */
      const currentQuantity = Number(editData?.quantity ?? 0);

      const availableBalance = Number(balanceQuantity);

      if (!Number.isNaN(availableBalance)) {
        setMaxQuantity(availableBalance + currentQuantity);
      } else {
        setMaxQuantity("");
      }
    } catch (e) {
      console.log(e, "get maximum quantity error");
      setMaxQuantity("");
    } finally {
      setLoadingMaxQuantity(false);
    }
  };

  // Quantity change
  const handleQuantityChange = (e) => {
    const value = e.target.value;

    // Allow clearing the field
    if (value === "") {
      setQuantity("");
      return;
    }

    const numericValue = Number(value);

    if (numericValue < 0) {
      return;
    }

    // Maximum quantity validation
    if (
      maxQuantity !== "" &&
      numericValue > Number(maxQuantity)
    ) {
      toast.warn(`Max Quantity ${maxQuantity}`);
      return;
    }

    setQuantity(value);
  };

  const handlePartSubmit = async () => {
    if (quantity === "" || Number(quantity) <= 0) {
      toast.warning("Please enter a valid quantity");
      return;
    }

    // Final validation before API call
    if (
      maxQuantity !== "" &&
      Number(quantity) > Number(maxQuantity)
    ) {
      toast.warn(`Max Quantity ${maxQuantity}`);
      return;
    }

    setLoading(true);

    try {
      if (type === "wo") {
        const wotlink =
          masterAPI["workOrderTaskPart"].update;

        const wotData = {
          id: editData.id,
          workOrderTaskId: editData.workOrderTaskId,
          partId: editData.partId,
          stockRoomId: editData.stockRoomId,
          quantity: Number(quantity),
          standardPrice: editData.standardPrice ?? 0,
          plannedCost:
            (editData.standardPrice ?? 0) *
            Number(quantity),
        };

        console.log(
          "Work Order Part Update:",
          wotData
        );

        await axiosPrivate.put(
          `${wotlink}`,
          wotData
        );
      } else {
        const jptlink =
          masterAPI["jobPlanTaskParts"].update;

        const putData = {
          id: editData.id,
          jobPlanTaskId: editData.jobPlanTaskId,
          partId: editData.partId,
          stockRoomId: editData.stockRoomId,
          quantity: Number(quantity),
          standardPrice: editData.standardPrice ?? 0,
          plannedCost:
            (editData.standardPrice ?? 0) *
            Number(quantity),
        };

        console.log(
          "Job Plan Part Update:",
          putData
        );

        await axiosPrivate.put(
          `${jptlink}`,
          putData
        );
      }

      handleCloseClick();
    } catch (e) {
      console.log(e, "update part error");
    } finally {
      setLoading(false);
    }
  };

  const readOnlyFieldSx = {
    "& .MuiInputBase-input": {
      backgroundColor: "#f5f5f5",
      cursor: "not-allowed",
    },
  };

  return (
    <Grid
      container
      sx={{ p: 1 }}
      spacing={"1.5rem"}
    >
      {/* Header */}
      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: color.masterDialogTitleColor,
              fontSize: "1.1rem",
              fontWeight: 500,
            }}
          >
            Part Edit
          </Typography>

          <Box>
            {/* Save */}
            <IconButton
              disabled={
                loading ||
                loadingMaxQuantity
              }
              onClick={handlePartSubmit}
            >
              {loading ? (
                <CircularProgress
                  size={20}
                  sx={{ color: "#2362B4" }}
                />
              ) : (
                <Save
                  sx={{ color: "#2362B4" }}
                />
              )}
            </IconButton>

            {/* Close */}
            <IconButton
              disabled={loading}
              onClick={handleCloseClick}
            >
              <Clear
                sx={{ color: "#2362B4" }}
              />
            </IconButton>
          </Box>
        </Box>

        <Divider
          sx={{
            border:
              "1px solid rgb(0 0 0 / 17%)",
          }}
        />
      </Grid>

      {/* Part Details */}
      <Grid
        container
        item
        xs={12}
        spacing={"1.5rem"}
      >
        {/* Task No */}
        <Grid item xs={12} sm={6}>
          <Typography
            sx={{
              fontWeight:
                color.labelFontWeight,
              mb: color.labelmb,
              fontSize: color.labelFont,
              color: color.labelColor,
            }}
          >
            Task No
          </Typography>

          <TextField
            size="small"
            fullWidth
            value={
              editData?.taskNo ?? ""
            }
            InputProps={{
              readOnly: true,
            }}
            sx={readOnlyFieldSx}
          />
        </Grid>

        {/* Part Code */}
        <Grid item xs={12} sm={6}>
          <Typography
            sx={{
              fontWeight:
                color.labelFontWeight,
              mb: color.labelmb,
              fontSize: color.labelFont,
              color: color.labelColor,
            }}
          >
            Part Code
          </Typography>

          <TextField
            size="small"
            fullWidth
            value={
              editData?.partsCode ?? ""
            }
            InputProps={{
              readOnly: true,
            }}
            sx={readOnlyFieldSx}
          />
        </Grid>

        {/* Part Name */}
        <Grid item xs={12} sm={6}>
          <Typography
            sx={{
              fontWeight:
                color.labelFontWeight,
              mb: color.labelmb,
              fontSize: color.labelFont,
              color: color.labelColor,
            }}
          >
            Part Name
          </Typography>

          <TextField
            size="small"
            fullWidth
            value={
              editData?.partName ?? ""
            }
            InputProps={{
              readOnly: true,
            }}
            sx={readOnlyFieldSx}
          />
        </Grid>

        {/* Stockroom Name */}
        <Grid item xs={12} sm={6}>
          <Typography
            sx={{
              fontWeight:
                color.labelFontWeight,
              mb: color.labelmb,
              fontSize: color.labelFont,
              color: color.labelColor,
            }}
          >
            Stockroom Name
          </Typography>

          <TextField
            size="small"
            fullWidth
            value={
              editData?.stockRoomName ?? ""
            }
            InputProps={{
              readOnly: true,
            }}
            sx={readOnlyFieldSx}
          />
        </Grid>

        {/* Unit Cost */}
        <Grid item xs={12} sm={6}>
          <Typography
            sx={{
              fontWeight:
                color.labelFontWeight,
              mb: color.labelmb,
              fontSize: color.labelFont,
              color: color.labelColor,
            }}
          >
            Unit Cost
          </Typography>

          <TextField
            size="small"
            fullWidth
            value={
              editData?.standardPrice ?? ""
            }
            InputProps={{
              readOnly: true,
            }}
            sx={readOnlyFieldSx}
          />
        </Grid>

        {/* Planned Line Cost */}
        <Grid item xs={12} sm={6}>
          <Typography
            sx={{
              fontWeight:
                color.labelFontWeight,
              mb: color.labelmb,
              fontSize: color.labelFont,
              color: color.labelColor,
            }}
          >
            Planned Line Cost
          </Typography>

          <TextField
            size="small"
            fullWidth
            value={
              editData?.plannedCost ?? ""
            }
            InputProps={{
              readOnly: true,
            }}
            sx={readOnlyFieldSx}
          />
        </Grid>

        {/* Quantity */}
        <Grid item xs={12} sm={6}>
          <Typography
            sx={{
              fontWeight:
                color.labelFontWeight,
              mb: color.labelmb,
              fontSize: color.labelFont,
              color: color.labelColor,
            }}
          >
            Quantity
          </Typography>

          <TextField
            name="quantity"
            type="number"
            size="small"
            fullWidth
            placeholder="Enter Quantity"
            value={quantity}
            onChange={
              handleQuantityChange
            }
            inputProps={{
              min: 0,
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {loadingMaxQuantity
                    ? "..."
                    : maxQuantity !== ""
                    ? `Max: ${maxQuantity}`
                    : null}
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PartEdit;